import React from "react";
import { BarChart } from "@mui/x-charts/BarChart";
import { LineChart } from "@mui/x-charts/LineChart";
import { SeriesPoint } from "../analyticsApi";
import { bucketLabel, formatNumber, formatRs } from "../analyticsUtils";
import { chartSx, seriesColors, useIsDark } from "./ui";

export type SeriesSpec = { key: string; label: string };

const num = (p: SeriesPoint, k: string): number => {
  const v = p[k];
  return typeof v === "number" && Number.isFinite(v) ? v : 0;
};

const nullable = (p: SeriesPoint, k: string): number | null => {
  const v = p[k];
  return typeof v === "number" && Number.isFinite(v) ? v : null;
};

interface Props {
  series: SeriesPoint[];
  specs: SeriesSpec[];
  /** "rs" formats tooltips as rupees; "count" as plain numbers; "pct" adds %. */
  format?: "rs" | "count" | "pct";
  height?: number;
  stacked?: boolean;
}

function formatter(format: Props["format"]) {
  return (v: number | null) => (format === "rs" ? formatRs(v) : format === "pct" ? (v == null ? "—" : `${v.toFixed(1)}%`) : formatNumber(v));
}

/** Grouped (or stacked) bars over the period's buckets - one axis, fixed hues. */
export function PeriodBarChart({ series, specs, format = "count", height = 280, stacked = false }: Props) {
  const isDark = useIsDark();
  const colors = seriesColors(isDark);
  const fmt = formatter(format);
  return (
    <BarChart
      height={height}
      xAxis={[{ scaleType: "band", data: series.map((p) => bucketLabel(p.bucket)) }]}
      yAxis={[{ valueFormatter: (v: number) => (format === "rs" ? formatNumber(v) : String(v)) }]}
      series={specs.map((s, i) => ({
        id: s.key,
        label: s.label,
        data: series.map((p) => num(p, s.key)),
        color: colors[i % colors.length],
        valueFormatter: fmt,
        ...(stacked ? { stack: "total" } : {}),
      }))}
      borderRadius={4}
      grid={{ horizontal: true }}
      margin={{ left: 56, right: 12, top: 36, bottom: 32 }}
      sx={chartSx(isDark)}
    />
  );
}

/** Lines over the period's buckets; gaps where a value is null. */
export function PeriodLineChart({ series, specs, format = "count", height = 280 }: Props) {
  const isDark = useIsDark();
  const colors = seriesColors(isDark);
  const fmt = formatter(format);
  return (
    <LineChart
      height={height}
      xAxis={[{ scaleType: "point", data: series.map((p) => bucketLabel(p.bucket)) }]}
      yAxis={[{ valueFormatter: (v: number) => (format === "rs" ? formatNumber(v) : String(v)) }]}
      series={specs.map((s, i) => ({
        id: s.key,
        label: s.label,
        data: series.map((p) => nullable(p, s.key)),
        color: colors[i % colors.length],
        valueFormatter: fmt,
        showMark: series.length <= 31,
        connectNulls: false,
      }))}
      grid={{ horizontal: true }}
      margin={{ left: 56, right: 12, top: 36, bottom: 32 }}
      sx={chartSx(isDark)}
    />
  );
}

/** Simple categorical bars (weekdays, age bands, payment methods). */
export function CategoryBarChart({
  labels,
  values,
  label,
  format = "count",
  height = 240,
  horizontal = false,
}: {
  labels: string[];
  values: Array<number | null>;
  label: string;
  format?: Props["format"];
  height?: number;
  horizontal?: boolean;
}) {
  const isDark = useIsDark();
  const colors = seriesColors(isDark);
  const fmt = formatter(format);
  const axis = { scaleType: "band" as const, data: labels };
  return (
    <BarChart
      height={height}
      layout={horizontal ? "horizontal" : "vertical"}
      {...(horizontal ? { yAxis: [axis], xAxis: [{ valueFormatter: (v: number) => (format === "rs" ? formatNumber(v) : String(v)) }] } : { xAxis: [axis] })}
      series={[{ id: label, label, data: values.map((v) => v ?? 0), color: colors[0], valueFormatter: fmt }]}
      borderRadius={4}
      grid={horizontal ? { vertical: true } : { horizontal: true }}
      margin={{ left: horizontal ? 96 : 56, right: 12, top: 36, bottom: 32 }}
      slotProps={{ legend: { hidden: true } }}
      sx={chartSx(isDark)}
    />
  );
}
